import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'motion/react';
import { Plus, Trash2, Save, Loader2, Pencil, RotateCcw } from 'lucide-react';
import toast from 'react-hot-toast';
import { ResponsiveGridLayout, useContainerWidth, verticalCompactor } from 'react-grid-layout';
import type { Layout, LayoutItem, ResponsiveLayouts } from 'react-grid-layout';
import useMediaQuery, { MD_BREAKPOINT } from '@/hooks/useMediaQuery';
import { GlassCard } from '@/components/glass';
import { http } from '@/network/http';
import { useActiveRoles, useActiveEmploymentRoles } from '../hooks';
import {
  WIDGET_DEFS,
  WIDGET_MAP,
  DEFAULT_WIDGET_IDS,
  filterWidgetsByPermissions,
  type WidgetDef,
} from '@/features/home/components/widgets/widgetRegistry';
import { widgetApi } from '@/features/home/components/widgets/widgetApi';
import { WidgetWrapper } from '@/features/home/components/widgets/WidgetWrapper';
import { WidgetAddDrawer } from '@/features/home/components/widgets/WidgetAddDrawer';
import { HomeFilterProvider } from '@/features/home/components/HomeFilterContext';
import { HomeFilterBar } from '@/features/home/components/HomeFilterBar';
import { widgetStaggerContainer } from '@/features/home/components/widgets/widgetAnimations';
import { staggerContainer, staggerItem } from '../utils/adminAnimations';

import 'react-grid-layout/css/styles.css';

type TargetType = 'role' | 'employmentRole';

interface TemplateConfig {
  activeWidgetIds: string[];
  hiddenWidgetIds: string[];
  layouts: { lg: LayoutItem[]; md: LayoutItem[]; sm: LayoutItem[] };
}

interface Template {
  id: string;
  targetType: string;
  targetCode: string;
  config: TemplateConfig;
}

interface UserOption {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  avatarUrl?: string | null;
}

function buildColumnLayout(ids: string[], cols: number): LayoutItem[] {
  const items: LayoutItem[] = [];
  let y = 0;
  let pendingHalf: { h: number } | null = null;

  ids.forEach((id) => {
    const def = WIDGET_MAP.get(id);
    if (!def) return;
    const { w, h } = def.defaultSize;

    if (cols === 1) {
      items.push({ i: id, x: 0, y, w: 1, h, minW: 1, minH: def.minSize.h });
      y += h;
      return;
    }

    if (w >= 12) {
      if (pendingHalf) { y += pendingHalf.h; pendingHalf = null; }
      items.push({ i: id, x: 0, y, w: 12, h, minW: def.minSize.w, minH: def.minSize.h });
      y += h;
    } else if (!pendingHalf) {
      items.push({ i: id, x: 0, y, w: 6, h, minW: def.minSize.w, minH: def.minSize.h });
      pendingHalf = { h };
    } else {
      items.push({ i: id, x: 6, y, w: 6, h, minW: def.minSize.w, minH: def.minSize.h });
      y += Math.max(pendingHalf.h, h);
      pendingHalf = null;
    }
  });
  if (pendingHalf) y += pendingHalf.h;
  return items;
}

function buildDefaultLayouts(ids: string[]): TemplateConfig['layouts'] {
  const layout = buildColumnLayout(ids, 12);
  return { lg: layout, md: layout, sm: buildColumnLayout(ids, 1) };
}

function getPermittedWidgetsForRole(roleCode: string): WidgetDef[] {
  const isAdmin = ['SUPER_ADMIN', 'ADMIN'].includes(roleCode);
  return filterWidgetsByPermissions(WIDGET_DEFS, {
    canCreateReports: true,
    isAdmin,
    hasPermission: isAdmin ? () => true : () => false,
  });
}

function getPermittedWidgetsForEmploymentRole(): WidgetDef[] {
  return filterWidgetsByPermissions(WIDGET_DEFS, {
    canCreateReports: true,
    isAdmin: false,
    hasPermission: () => false,
  });
}

function useMeasure(ref: React.RefObject<HTMLDivElement | null>) {
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setSize({ w: Math.round(entry.contentRect.width), h: Math.round(entry.contentRect.height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return size;
}

function GridItem({ widget, isEditMode, isMobile, removeWidget, handleExpand, availableWidgets, setDrawerOpen }: {
  widget: WidgetDef;
  isEditMode: boolean;
  isMobile: boolean;
  removeWidget: (id: string) => void;
  handleExpand: (id: string) => void;
  availableWidgets: WidgetDef[];
  setDrawerOpen: (open: boolean) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const size = useMeasure(ref);
  return (
    <div ref={ref} style={{ width: '100%', height: '100%' }}>
      <WidgetWrapper
        def={widget}
        isEditMode={isEditMode}
        isMobile={isMobile}
        width={size.w}
        height={size.h}
        onRemove={removeWidget}
        onExpand={handleExpand}
        onOpenAddDrawer={availableWidgets.length > 0 ? () => setDrawerOpen(true) : undefined}
      />
    </div>
  );
}

export function WidgetLayoutManagerPage() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const isMobile = useMediaQuery(MD_BREAKPOINT);

  // Tab state
  const [targetType, setTargetType] = useState<TargetType>('role');
  const [selectedCode, setSelectedCode] = useState<string>('');
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [isEditMode, setIsEditMode] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expandedWidget, setExpandedWidget] = useState<string | null>(null);

  const handleExpand = useCallback((id: string) => {
    setExpandedWidget(id);
  }, []);

  const handleCollapseExpand = useCallback(() => {
    setExpandedWidget(null);
  }, []);

  // Close expanded widget on Escape
  useEffect(() => {
    if (!expandedWidget) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExpandedWidget(null);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [expandedWidget]);

  // Fetch roles & employment roles
  const { data: roles } = useActiveRoles();
  const { data: empRoles } = useActiveEmploymentRoles();

  // Fetch all templates
  const { data: templates } = useQuery({
    queryKey: ['widget-layout-templates'],
    queryFn: async () => {
      const { data } = await widgetApi.getTemplates();
      return data.templates as Template[];
    },
  });

  // Fetch users filtered by role/empRole
  const { data: usersData } = useQuery({
    queryKey: ['admin-widget-users', targetType, selectedCode],
    queryFn: async () => {
      const params: Record<string, string> = { status: 'ACTIVE', limit: '100' };
      if (targetType === 'role') {
        params.role = selectedCode;
      } else {
        params.employmentRoleCode = selectedCode;
      }
      const { data } = await http.get<{ success: boolean; users: UserOption[] }>('/users', { params });
      return data.users;
    },
    enabled: !!selectedCode,
  });

  // Auto-select first option when tab or data changes
  useEffect(() => {
    if (targetType === 'role' && roles?.length && !selectedCode) {
      setSelectedCode(roles[0].code);
    } else if (targetType === 'employmentRole' && empRoles?.length && !selectedCode) {
      setSelectedCode(empRoles[0].code);
    }
  }, [targetType, roles, empRoles, selectedCode]);

  // Reset selectedCode and user when switching tabs
  const handleTabChange = useCallback((type: TargetType) => {
    setTargetType(type);
    setSelectedCode('');
    setSelectedUserId('');
  }, []);

  // Handle role/empRole change
  const handleCodeChange = useCallback((code: string) => {
    setSelectedCode(code);
    setSelectedUserId('');
  }, []);

  // Find existing template for current selection
  const existingTemplate = useMemo(
    () => templates?.find((tmpl) => tmpl.targetType === targetType && tmpl.targetCode === selectedCode),
    [templates, targetType, selectedCode],
  );

  // Determine permitted widgets for current selection
  const permittedWidgets = useMemo(() => {
    if (targetType === 'employmentRole') return getPermittedWidgetsForEmploymentRole();
    return getPermittedWidgetsForRole(selectedCode);
  }, [targetType, selectedCode]);

  const permittedIds = useMemo(() => permittedWidgets.map((w) => w.id), [permittedWidgets]);

  // Local editor state
  const [activeIds, setActiveIds] = useState<string[]>([]);
  const [layouts, setLayouts] = useState<TemplateConfig['layouts']>({ lg: [], md: [], sm: [] });
  const configRef = useRef({ activeIds, layouts });
  configRef.current = { activeIds, layouts };

  // Sync editor state from template or defaults when selection changes
  useEffect(() => {
    if (!selectedCode) return;
    if (existingTemplate) {
      const validIds = existingTemplate.config.activeWidgetIds.filter((id) => permittedIds.includes(id));
      setActiveIds(validIds);
      setLayouts(existingTemplate.config.layouts ?? buildDefaultLayouts(validIds));
    } else {
      const defaultIds = DEFAULT_WIDGET_IDS.filter((id) => permittedIds.includes(id));
      setActiveIds(defaultIds);
      setLayouts(buildDefaultLayouts(defaultIds));
    }
  }, [selectedCode, existingTemplate, permittedIds]);

  // Available (not yet active) widgets
  const availableWidgets = useMemo(() => {
    const activeSet = new Set(activeIds);
    return permittedWidgets.filter((w) => !activeSet.has(w.id));
  }, [activeIds, permittedWidgets]);

  // Active widget defs
  const activeWidgets = useMemo(
    () => activeIds.map((id) => WIDGET_MAP.get(id)).filter(Boolean) as WidgetDef[],
    [activeIds],
  );

  // Grid container
  const { width, containerRef, mounted } = useContainerWidth();

  // Layout change handler
  const handleLayoutChange = useCallback((_layout: Layout, allLayouts: ResponsiveLayouts) => {
    setLayouts({
      lg: [...(allLayouts.lg ?? configRef.current.layouts.lg)] as LayoutItem[],
      md: [...(allLayouts.md ?? configRef.current.layouts.md)] as LayoutItem[],
      sm: [...(allLayouts.sm ?? configRef.current.layouts.sm)] as LayoutItem[],
    });
  }, []);

  // Add widget
  const handleAddWidget = useCallback((id: string) => {
    const def = WIDGET_MAP.get(id);
    if (!def) return;
    const newItem: LayoutItem = {
      i: id, x: 0, y: Infinity,
      w: def.defaultSize.w, h: def.defaultSize.h,
      minW: def.minSize.w, minH: def.minSize.h,
    };
    setActiveIds((prev) => [...prev, id]);
    setLayouts((prev) => ({
      lg: [...prev.lg, newItem],
      md: [...prev.md, newItem],
      sm: [...prev.sm, { ...newItem, w: 1, x: 0 }],
    }));
  }, []);

  // Remove widget
  const handleRemoveWidget = useCallback((id: string) => {
    setActiveIds((prev) => prev.filter((i) => i !== id));
    setLayouts((prev) => ({
      lg: prev.lg.filter((l) => l.i !== id),
      md: prev.md.filter((l) => l.i !== id),
      sm: prev.sm.filter((l) => l.i !== id),
    }));
  }, []);

  // Reset to defaults
  const handleResetToDefaults = useCallback(() => {
    const defaultIds = DEFAULT_WIDGET_IDS.filter((id) => permittedIds.includes(id));
    setActiveIds(defaultIds);
    setLayouts(buildDefaultLayouts(defaultIds));
  }, [permittedIds]);

  // Save confirmation dialog
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [overrideExisting, setOverrideExisting] = useState(false);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (opts: { overrideExisting: boolean }) => {
      const config: TemplateConfig = {
        activeWidgetIds: configRef.current.activeIds,
        hiddenWidgetIds: permittedIds.filter((id) => !configRef.current.activeIds.includes(id)),
        layouts: configRef.current.layouts,
      };
      return widgetApi.upsertTemplate(targetType, selectedCode, config, opts.overrideExisting);
    },
    onSuccess: (res) => {
      const count = res.data?.overriddenCount ?? 0;
      if (count > 0) {
        toast.success(t('admin.widgetLayouts.savedWithOverride', { count }));
      } else {
        toast.success(t('admin.widgetLayouts.saved'));
      }
      queryClient.invalidateQueries({ queryKey: ['widget-layout-templates'] });
      setShowSaveConfirm(false);
      setOverrideExisting(false);
    },
  });

  const handleSaveClick = useCallback(() => {
    setOverrideExisting(false);
    setShowSaveConfirm(true);
  }, []);

  const handleConfirmSave = useCallback(() => {
    saveMutation.mutate({ overrideExisting });
  }, [overrideExisting, saveMutation]);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => widgetApi.deleteTemplate(targetType, selectedCode),
    onSuccess: () => {
      toast.success(t('admin.widgetLayouts.deleted'));
      queryClient.invalidateQueries({ queryKey: ['widget-layout-templates'] });
    },
  });

  const isHebrew = i18n.language === 'he';
  const pickName = (r: { name: string; nameEn?: string | null }) =>
    isHebrew ? r.name : (r.nameEn || r.name);

  const options = targetType === 'role'
    ? (roles ?? []).map((r: { code: string; name: string; nameEn?: string | null }) => ({ code: r.code, label: pickName(r) }))
    : (empRoles ?? []).map((r: { code: string; name: string; nameEn?: string | null }) => ({ code: r.code, label: pickName(r) }));

  return (
    <motion.div
      className="space-y-6 p-4 md:p-6"
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
    >
      {/* Header */}
      <motion.div variants={staggerItem}>
        <h1 className="text-2xl font-bold text-[var(--color-neutral-900)]">
          {t('admin.widgetLayouts.title')}
        </h1>
        <p className="mt-1 text-sm text-[var(--color-neutral-500)]">
          {t('admin.widgetLayouts.subtitle')}
        </p>
      </motion.div>

      {/* Tab Selector */}
      <motion.div variants={staggerItem} className="inline-flex rounded-full bg-[var(--color-neutral-100)] p-1">
        {(['role', 'employmentRole'] as const).map((type) => (
          <button
            key={type}
            className={`relative rounded-full px-5 py-2 text-sm font-semibold transition-all ${
              targetType === type
                ? 'bg-primary-100 text-primary-700 shadow-sm'
                : 'text-[var(--color-neutral-500)] hover:text-[var(--color-neutral-700)]'
            }`}
            onClick={() => handleTabChange(type)}
          >
            {t(type === 'role' ? 'admin.widgetLayouts.byRole' : 'admin.widgetLayouts.byEmploymentRole')}
          </button>
        ))}
      </motion.div>

      {/* Role Selector + User Selector */}
      <motion.div variants={staggerItem}>
        <GlassCard className="p-4">
          <div className="grid gap-4 md:grid-cols-2">
            {/* Role picker */}
            <div>
              <label className="mb-2 block text-sm font-medium text-[var(--color-neutral-700)]">
                {t('admin.widgetLayouts.selectTarget')}
              </label>
              <select
                className="w-full rounded-xl border border-[var(--color-neutral-200)] bg-white/60 px-3 py-2 text-sm backdrop-blur-sm"
                value={selectedCode}
                onChange={(e) => handleCodeChange(e.target.value)}
              >
                {options.map((opt) => (
                  <option key={opt.code} value={opt.code}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* User picker (data source) */}
            <div>
              <label className="mb-2 block text-sm font-medium text-[var(--color-neutral-700)]">
                {t('admin.widgetLayouts.selectUser')}
                <span className="ms-1 text-xs text-[var(--color-neutral-400)]">({t('admin.widgetLayouts.dataSource')})</span>
              </label>
              <select
                className="w-full rounded-xl border border-[var(--color-neutral-200)] bg-white/60 px-3 py-2 text-sm backdrop-blur-sm disabled:opacity-50"
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                disabled={!usersData?.length}
              >
                <option value="">{usersData?.length ? `-- ${t('admin.widgetLayouts.selectUser')} --` : t('admin.widgetLayouts.noUsers')}</option>
                {(usersData ?? []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.firstName} {u.lastName} ({u.email})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Template status */}
          {selectedCode && (
            <p className="mt-3 text-xs text-[var(--color-neutral-500)]">
              {existingTemplate
                ? `${t('admin.widgetLayouts.preview')}: ${activeIds.length} widgets`
                : t('admin.widgetLayouts.noTemplate')}
            </p>
          )}
        </GlassCard>
      </motion.div>

      {/* Edit mode toggle */}
      {selectedCode && (
        <motion.div variants={staggerItem} className="flex items-center gap-3">
          <motion.button
            className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-all ${
              isEditMode
                ? 'bg-[var(--color-primary-500)] text-white shadow-md'
                : 'bg-white/60 text-[var(--color-neutral-600)] backdrop-blur-sm'
            }`}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsEditMode(!isEditMode)}
          >
            <Pencil size={14} />
            {isEditMode ? t('admin.widgetLayouts.editMode') : t('admin.widgetLayouts.previewMode')}
          </motion.button>
        </motion.div>
      )}

      {/* Visual Grid Editor — Live Widgets (1:1 with WidgetGrid) */}
      {selectedCode && (
        <HomeFilterProvider previewUserId={selectedUserId || undefined}>
          {selectedUserId && <HomeFilterBar />}
          <motion.div
            ref={containerRef as React.RefObject<HTMLDivElement>}
            className="mt-6 px-4 md:px-10"
            data-edit-zone
            variants={widgetStaggerContainer}
            initial="hidden"
            animate="visible"
          >
            {mounted && activeWidgets.length > 0 && (
              <ResponsiveGridLayout
                width={width}
                breakpoints={{ lg: 1200, md: 768, sm: 0 }}
                cols={{ lg: 12, md: 12, sm: 1 }}
                rowHeight={150}
                layouts={layouts}
                onLayoutChange={handleLayoutChange}
                dragConfig={{
                  enabled: !isMobile,
                  handle: '.widget-drag-handle',
                }}
                resizeConfig={{
                  enabled: !isMobile,
                }}
                compactor={verticalCompactor}
                margin={[24, 24]}
                containerPadding={[0, 0]}
              >
                {activeWidgets.map((widget) => (
                  <div key={widget.id}>
                    <GridItem
                      widget={widget}
                      isEditMode={isEditMode}
                      isMobile={isMobile}
                      removeWidget={handleRemoveWidget}
                      handleExpand={handleExpand}
                      availableWidgets={availableWidgets}
                      setDrawerOpen={setDrawerOpen}
                    />
                  </div>
                ))}
              </ResponsiveGridLayout>
            )}
            {activeWidgets.length === 0 && (
              <div className="flex h-[200px] items-center justify-center text-sm text-[var(--color-neutral-400)]">
                {t('admin.widgetLayouts.noWidgets')}
              </div>
            )}

            {/* Edit mode actions */}
            {isEditMode && (
              <motion.div
                className="mt-4 flex items-center justify-center gap-3"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <motion.button
                  className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold shadow-lg backdrop-blur-sm ${
                    availableWidgets.length > 0
                      ? 'bg-primary-500/90 text-white'
                      : 'bg-gray-300/60 text-gray-400 cursor-not-allowed'
                  }`}
                  whileTap={availableWidgets.length > 0 ? { scale: 0.95 } : undefined}
                  onClick={() => availableWidgets.length > 0 && setDrawerOpen(true)}
                >
                  <Plus size={16} />
                  {t('home.widgets.addWidget')}
                </motion.button>
                <motion.button
                  className="flex items-center gap-1.5 rounded-full bg-white/60 px-3 py-2 text-sm font-medium text-gray-600 shadow-sm backdrop-blur-sm"
                  whileTap={{ scale: 0.95 }}
                  onClick={handleResetToDefaults}
                >
                  <RotateCcw size={14} />
                  {t('home.widgets.resetLayout')}
                </motion.button>
              </motion.div>
            )}
          </motion.div>
        </HomeFilterProvider>
      )}

      {/* Actions */}
      {selectedCode && (
        <motion.div variants={staggerItem} className="flex gap-3">
          <button
            className="flex items-center gap-2 rounded-xl bg-[var(--color-primary-500)] px-5 py-2.5 text-sm font-semibold text-white shadow-md transition-colors hover:bg-[var(--color-primary-600)] disabled:opacity-50"
            onClick={handleSaveClick}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {t('admin.widgetLayouts.save')}
          </button>
        </motion.div>
      )}

      {/* Save confirmation dialog */}
      <AnimatePresence>
        {showSaveConfirm && (
          <motion.div
            key="save-confirm-overlay"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowSaveConfirm(false)}
          >
            <motion.div
              className="glass-surface-widget mx-4 w-full max-w-md rounded-2xl p-6 shadow-xl"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold text-[var(--color-neutral-900)]">
                {t('admin.widgetLayouts.saveConfirmTitle')}
              </h3>
              <p className="mt-2 text-sm text-[var(--color-neutral-600)]">
                {t('admin.widgetLayouts.saveConfirmDescription')}
              </p>

              {/* Override checkbox */}
              <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-xl border border-[var(--color-neutral-200)] bg-white/40 p-3 transition-colors hover:bg-white/60">
                <input
                  type="checkbox"
                  checked={overrideExisting}
                  onChange={(e) => setOverrideExisting(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-[var(--color-neutral-300)] text-[var(--color-primary-500)] accent-[var(--color-primary-500)]"
                />
                <div>
                  <span className="text-sm font-medium text-[var(--color-neutral-800)]">
                    {t('admin.widgetLayouts.overrideExisting')}
                  </span>
                  <p className="mt-0.5 text-xs text-[var(--color-neutral-500)]">
                    {t('admin.widgetLayouts.overrideExistingHint')}
                  </p>
                </div>
              </label>

              {/* Actions */}
              <div className="mt-5 flex justify-end gap-3">
                <button
                  className="rounded-xl px-4 py-2 text-sm font-medium text-[var(--color-neutral-600)] transition-colors hover:bg-[var(--color-neutral-100)]"
                  onClick={() => setShowSaveConfirm(false)}
                >
                  {t('common.cancel')}
                </button>
                <button
                  className="flex items-center gap-2 rounded-xl bg-[var(--color-primary-500)] px-5 py-2 text-sm font-semibold text-white shadow-md transition-colors hover:bg-[var(--color-primary-600)] disabled:opacity-50"
                  onClick={handleConfirmSave}
                  disabled={saveMutation.isPending}
                >
                  {saveMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                  {t('admin.widgetLayouts.confirmSave')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add widget drawer */}
      <WidgetAddDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        widgets={availableWidgets}
        onAdd={handleAddWidget}
      />

      {/* Fullscreen expand overlay (1:1 with WidgetGrid) */}
      <AnimatePresence>
        {expandedWidget && (() => {
          const def = WIDGET_MAP.get(expandedWidget);
          if (!def) return null;
          const Component = def.component;
          return (
            <motion.div
              key="widget-focus-overlay"
              className="widget-focus-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleCollapseExpand}
            >
              <motion.div
                className="glass-surface-widget m-4 h-[calc(100%-2rem)] w-[calc(100%-2rem)] overflow-auto rounded-[32px] p-6 md:m-8 md:h-[calc(100%-4rem)] md:w-[calc(100%-4rem)]"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                onClick={(e) => e.stopPropagation()}
              >
                <Component width={800} height={600} isEditMode={false} />
              </motion.div>
            </motion.div>
          );
        })()}
      </AnimatePresence>
    </motion.div>
  );
}
